import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Grid,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "./FAQ.module.css";
import { Helmet } from "react-helmet";

const FAQ = () => {
  const faqData = [
    {
      question: "What is AI Image Colorizer?",
      answer:
        "AI Image Colorizer is a powerful tool that uses artificial intelligence to automatically add realistic colors to black-and-white or grayscale images. It can also restore and enhance old or faded photos.",
    },
    {
      question: "What types of images can I colorize with this AI?",
      answer:
        "You can colorize a wide range of images, including old family photos, historical images, portraits, landscapes, and more. The AI works best with clear, high-quality scans of black-and-white or grayscale images.",
    },
    {
      question: "How accurate is the colorization process?",
      answer:
        "Our AI uses advanced algorithms to predict and apply accurate colors based on the content of the image. While results are highly realistic, some manual adjustments may be needed for specific details.",
    },
    {
      question: "Can I edit the colors after the AI colorizes the image?",
      answer:
        "Yes, you can manually adjust colors, tones, and saturation after the AI completes the colorization process. Our platform provides easy-to-use editing tools for fine-tuning.",
    },
    {
      question: "How long does it take to colorize an image?",
      answer:
        "Colorization typically takes between 10 to 30 seconds, depending on the image size and complexity. Larger or more detailed images may take slightly longer.",
    },
    {
      question: "What file formats are supported for upload and download?",
      answer:
        "We support common image formats like JPEG, PNG, and TIFF for uploads. Colorized images can be downloaded in high-resolution formats, including JPEG and PNG.",
    },
    {
      question: "Can I use colorized images for commercial purposes?",
      answer:
        "Yes, all images colorized with our platform are royalty-free and can be used for personal or commercial projects, including marketing, publications, and more.",
    },
    {
      question: "Does the AI work on low-quality or damaged photos?",
      answer:
        "Yes, the AI can enhance and restore low-quality or damaged photos to some extent. However, results may vary depending on the level of damage or degradation in the original image.",
    },
    {
      question: "Is my data safe when using AI Image Colorizer?",
      answer:
        "Absolutely. We prioritize user privacy and data security. Uploaded images are processed securely and are not stored on our servers after the colorization is complete.",
    },
    {
      question: "What resolutions are supported for colorized images?",
      answer:
        "We support resolutions from standard sizes (e.g., 512x512) up to 4K UHD (3840x2160). Higher resolutions may require premium credits or subscriptions.",
    },
    {
      question: "Can I colorize multiple images at once?",
      answer:
        "Yes, our platform supports batch processing, allowing you to upload and colorize multiple images simultaneously for faster results.",
    },
  ];

  // Create schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Helmet>
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Typography variant="h2" className={styles.heading}>
        Frequently Asked Questions
      </Typography>
      <Typography variant="h6" className={styles.subheading}>
        Can't find the answer you're looking for? Reach out to our customer
        support team
      </Typography>

      <Grid item xs={12} md={8}>
        {faqData.map((faq, index) => (
          <Accordion key={index} className={styles.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography className={styles.question}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={styles.answer}>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Container>
  );
};

export default FAQ;
